import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import styled, { keyframes } from "styled-components";
import { registerUser } from "../../redux/apiCalls";
import theme from "../../theme";
import { mobile } from "../../responsive";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Page = styled.div`
  min-height: 100vh;
  background: ${theme.bg};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const Card = styled.div`
  background: ${theme.cardBg};
  border-radius: 14px;
  border: 1.5px solid ${theme.cardBorder};
  box-shadow: 0 4px 24px rgba(26, 18, 8, 0.08);
  width: 100%;
  max-width: 420px;
  overflow: hidden;
  animation: ${fadeIn} 0.4s var(--ease-out-expo, ease) both;

  ${mobile(`max-width: 100%;`)}
`;

const TopBar = styled.div`
  height: 5px;
  background: ${theme.primary};
`;

const CardBody = styled.div`
  padding: 40px 36px 36px;
  ${mobile(`padding: 28px 20px;`)}
`;

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 28px;
`;

const BrandMark = styled.div`
  width: 30px;
  height: 30px;
  background: ${theme.textMain};
  color: ${theme.cardBg};
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-display, 'Georgia', serif);
  font-style: italic;
  font-size: 15px;
`;

const BrandName = styled.span`
  font-family: var(--font-display, 'Georgia', serif);
  font-size: 16px;
  color: ${theme.textMain};
  letter-spacing: -0.2px;
`;

const Title = styled.h1`
  font-family: var(--font-display, 'Georgia', serif);
  font-size: 26px;
  font-weight: 400;
  color: ${theme.textMain};
  margin: 0 0 6px;
  letter-spacing: -0.5px;
  line-height: 1.2;
`;

const Subtitle = styled.p`
  font-size: 13.5px;
  color: ${theme.textMuted};
  margin: 0 0 24px;
  line-height: 1.5;
`;

const Divider = styled.div`
  height: 1px;
  background: ${theme.separator};
  margin: 0 0 20px;
`;

const FieldGroup = styled.div`
  margin-bottom: 14px;
`;

const Label = styled.label`
  display: block;
  font-size: 11.5px;
  font-weight: 700;
  color: ${theme.textMuted};
  margin-bottom: 5px;
  text-transform: uppercase;
  letter-spacing: 0.8px;
`;

const Input = styled.input`
  width: 100%;
  background: ${theme.bg};
  border: 1.5px solid ${theme.cardBorder};
  border-radius: 7px;
  padding: 10px 14px;
  font-size: 14px;
  font-family: inherit;
  color: ${theme.textMain};
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.15s, box-shadow 0.15s;

  &::placeholder {
    color: ${theme.textMuted};
  }

  &:focus {
    border-color: ${theme.primary};
    box-shadow: 0 0 0 3px ${theme.primaryGlow};
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  background: ${(p) => (p.disabled ? theme.cardBorderStrong : theme.textMain)};
  color: ${theme.cardBg};
  border: none;
  border-radius: 8px;
  padding: 12px 28px;
  font-size: 14.5px;
  font-weight: 600;
  font-family: inherit;
  cursor: ${(p) => (p.disabled ? "not-allowed" : "pointer")};
  margin-top: 8px;
  transition: background 0.2s, transform 0.1s;

  &:hover:not(:disabled) {
    background: ${theme.primary};
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const ErrorMsg = styled.p`
  font-size: 13px;
  color: ${theme.danger};
  margin-top: 10px;
  text-align: center;
  background: ${theme.dangerLight};
  border-radius: 6px;
  padding: 8px 12px;
`;

const Footer = styled.p`
  font-size: 13px;
  color: ${theme.textMuted};
  text-align: center;
  margin-top: 22px;

  a {
    color: ${theme.primary};
    font-weight: 600;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

export default function Register() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    institution: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [serverError, setServerError] = useState("");

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (e.target.name === "confirmPassword" || e.target.name === "password") {
      setPasswordError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirmPassword) {
      setPasswordError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    setServerError("");
    try {
      const { confirmPassword, ...payload } = form;
      await registerUser(dispatch, payload);
      navigate("/");
    } catch (err) {
      const msg = err?.response?.data?.message;
      setServerError(msg || "Registration failed. Please check your details and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Page>
      <Card>
        <TopBar />
        <CardBody>
          <Brand>
            <BrandMark>N</BrandMark>
            <BrandName>NotesHub</BrandName>
          </Brand>

          <Title>Create account</Title>
          <Subtitle>Join NotesHub and start sharing your notes.</Subtitle>
          <Divider />

          <form onSubmit={handleSubmit}>
            <FieldGroup>
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                name="username"
                type="text"
                placeholder="yourname"
                value={form.username}
                onChange={handleChange}
                required
              />
            </FieldGroup>

            <FieldGroup>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="you@example.com"
                value={form.email}
                onChange={handleChange}
                required
              />
            </FieldGroup>

            <FieldGroup>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                placeholder="Choose a password"
                value={form.password}
                onChange={handleChange}
                required
              />
            </FieldGroup>

            <FieldGroup>
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                placeholder="Repeat your password"
                value={form.confirmPassword}
                onChange={handleChange}
                required
              />
            </FieldGroup>

            <FieldGroup>
              <Label htmlFor="institution">Institution (optional)</Label>
              <Input
                id="institution"
                name="institution"
                type="text"
                placeholder="Your university or school"
                value={form.institution}
                onChange={handleChange}
              />
            </FieldGroup>

            <SubmitButton type="submit" disabled={submitting}>
              {submitting ? "Creating account…" : "Create Account"}
            </SubmitButton>
          </form>

          {passwordError && <ErrorMsg>{passwordError}</ErrorMsg>}
          {serverError && <ErrorMsg>{serverError}</ErrorMsg>}

          <Footer>
            Already have an account? <Link to="/login">Sign in</Link>
          </Footer>
        </CardBody>
      </Card>
    </Page>
  );
}
